import { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../apiConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
} from "recharts";

const InsightBlock = ({
  description,
  summary,
}: {
  description?: string;
  summary?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <p className="text-sm text-slate-600">{description}</p>
    {summary ? (
      <div className="mt-3 border-l-4 border-slate-400 pl-3">
        <p className="text-xs font-medium text-slate-700">Key insight</p>
        <p className="text-sm text-slate-700">{summary}</p>
      </div>
    ) : null}
  </div>
);

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      {headerRight}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

type InsightData = {
  description?: string;
  summary?: string;
  data?: any;
};

export default function ChannelTab() {
  const [channelProfitability, setChannelProfitability] = useState<InsightData | null>(null);
  const [skuChannelPerformance, setSkuChannelPerformance] = useState<InsightData | null>(null);
  const [pricingOpportunities, setPricingOpportunities] = useState<InsightData | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchChannelInsights() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/channel-insights`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();

        setChannelProfitability(data.channel_profitability);
        setSkuChannelPerformance(data.sku_channel_performance);
        setPricingOpportunities(data.pricing_opportunities);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchChannelInsights();
  }, []);

  // Channels list for selector
  const channels = useMemo(() => Object.keys(skuChannelPerformance?.data ?? {}), [skuChannelPerformance]);
  useEffect(() => {
    if (channels.length && !selectedChannel) setSelectedChannel(channels[0]);
  }, [channels, selectedChannel]);

  // Formatters
  const formatChannelProfitabilityData = (data: any) =>
    Array.isArray(data?.data)
      ? data.data.map((item: any) => ({
          name: item.channel_name,
          sales: Number(item.sales_value ?? 0),
          margin: Number(item.gross_margin ?? 0),
        }))
      : [];

  const formatSkuPerformanceData = (data: any, channel: string) =>
    Array.isArray(data?.data?.[channel])
      ? data.data[channel].map((item: any) => ({
          name: item.sku_id,
          quantity: Number(item.qty ?? 0),
          sales: Number(item.sales_value ?? 0),
        }))
      : [];

  // Grouped by channel: { sku_id, [channelA]: n, [channelB]: n, ... }
  const formatPricingOpportunitiesData = (d: any) => {
    const rows: any[] = Array.isArray(d?.data) ? d.data : [];
    const bySku: Record<string, any> = {};
    for (const r of rows) {
      const sku = r.sku_id;
      if (!bySku[sku]) bySku[sku] = { sku_id: sku };
      bySku[sku][r.channel_name] = Number(r.avg_margin ?? 0);
    }
    return Object.values(bySku);
  };
  const uniquePricingChannels: string[] = useMemo(
    () => Array.from(new Set((pricingOpportunities?.data ?? []).map((x: any) => x.channel_name))),
    [pricingOpportunities]
  );

  const EmptyState = ({ label }: { label: string }) => (
    <div className="flex h-[260px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500">
      {label}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="h-6 w-56 animate-pulse rounded bg-slate-200" />
        <div className="mt-6 h-64 w-full animate-pulse rounded-2xl bg-slate-200" />
      </div>
    );
  }
  if (error) return <div className="p-6 md:p-8 text-red-600">Error: {error}</div>;

  return (
    <div className="p-6 md:p-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Channel Insights</h1>
      </div>

      {/* Channel Profitability */}
      <Card title="Channel Profitability" subtitle="Sales value vs. gross margin by channel">
        <div className="mb-5">
          <InsightBlock
            description={channelProfitability?.description}
            summary={channelProfitability?.summary}
          />
        </div>
        <div className="h-[320px] w-full">
          {formatChannelProfitabilityData(channelProfitability).length === 0 ? (
            <EmptyState label="No channel profitability data." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={formatChannelProfitabilityData(channelProfitability)}
                layout="vertical" // Horizontal bars
                margin={{ top: 10, right: 10, left: 40, bottom: 0 }}
                barCategoryGap="30%"
                barGap={8}
                barSize={24}
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: "#6B7280", fontSize: 13, fontWeight: 500 }}
                  width={120}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="sales" name="Sales Value" fill="#111827" radius={[0, 4, 4, 0]} />
                <Bar dataKey="margin" name="Gross Margin" fill="#9CA3AF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* SKU Channel Performance (with selector) */}
      <Card
        title="SKU Channel Performance"
        subtitle="Quantity sold and sales value per SKU"
        headerRight={
          channels.length ? (
            <select
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
            >
              {channels.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : null
        }
      >
        <div className="h-[320px] w-full">
          {formatSkuPerformanceData(skuChannelPerformance, selectedChannel).length === 0 ? (
            <EmptyState label="No SKU performance data." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={formatSkuPerformanceData(skuChannelPerformance, selectedChannel)}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  label={{ value: "Quantity", angle: -90, position: "insideLeft", fill: "#6B7280", fontSize: 12 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "#6B7280", fontSize: 12 }}
                  label={{ value: "Sales Value", angle: 90, position: "insideRight", fill: "#6B7280", fontSize: 12 }}
                />
                <Tooltip />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="quantity"
                  name="Quantity"
                  fill="#111827"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="sales"
                  name="Sales Value"
                  stroke="#D97706"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Pricing Opportunities */}
      <Card title="Pricing Opportunities" subtitle="Average margin by SKU across channels">
        <div className="mb-5">
          <InsightBlock
            description={pricingOpportunities?.description}
            summary={pricingOpportunities?.summary}
          />
        </div>
        <div className="h-[320px] w-full">
          {formatPricingOpportunitiesData(pricingOpportunities).length === 0 ? (
            <EmptyState label="No pricing opportunity data." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={formatPricingOpportunitiesData(pricingOpportunities)}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                barCategoryGap="20%"
              >
                <CartesianGrid stroke="#F3F4F6" strokeDasharray="3 3" />
                <XAxis dataKey="sku_id" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip />
                <Legend />
                {uniquePricingChannels.map((c, idx) => {
                  // Neutral grayscale palette for multiple channels
                  const palette = ["#111827", "#6B7280", "#9CA3AF", "#D1D5DB", "#374151"];
                  return (
                    <Bar
                      key={c}
                      dataKey={c}
                      name={`Avg Margin (${c})`}
                      fill={palette[idx % palette.length]}
                      radius={[4, 4, 0, 0]}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
}